import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, BookOpen, CalendarDays, Plus, AlertTriangle, ChevronRight, X } from 'lucide-react';
import { checkAILimit, recordAIUsage } from '../utils/rateLimit';
import { getSupabase } from '../services/supabase';

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatDate = () => {
  const d = new Date();
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

const todayKey = () => new Date().toISOString().split('T')[0];

const getExpiryDays = (expiryDate) => {
  if (!expiryDate) return null;
  return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const MacroRing = ({ consumed, target }) => {
  const pct = target > 0 ? Math.min(consumed / target, 1) : 0;
  const r = 34;
  const c = 2 * Math.PI * r;
  const dash = c * pct;

  return (
    <div className="macro-ring-container">
      <svg viewBox="0 0 80 80" className="macro-ring-svg" aria-hidden="true">
        <circle cx="40" cy="40" r={r} fill="none" stroke="var(--border-subtle)" strokeWidth="5" />
        {consumed > 0 && (
          <circle cx="40" cy="40" r={r} fill="none" stroke="var(--saffron)" strokeWidth="5"
            strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
            transform="rotate(-90 40 40)" />
        )}
      </svg>
      <div className="macro-ring-label">
        <span className="macro-ring-value">{consumed}</span>
        <span className="macro-ring-unit">/ {target} cal</span>
      </div>
    </div>
  );
};

const CUISINES = [
  '', 'Italian', 'Mexican', 'Japanese', 'Chinese', 'Indian', 'Thai',
  'Mediterranean', 'American', 'French', 'Korean', 'Middle Eastern',
];

const Home = ({
  blogPosts = [], setBlogPosts, inventory = [], tier,
  addToShoppingList, addRecipe, earnedMarks = [],
  mealPlan = {}, macroPlanner = {}, recipes = [], user,
}) => {
  const navigate = useNavigate();
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [aiPosts, setAiPosts] = useState([]);
  const [communityPosts, setCommunityPosts] = useState([]);

  useEffect(() => {
    getSupabase().then((db) => {
      db.from('blog_posts')
        .select('title, slug, focus_ingredients, published_at')
        .order('published_at', { ascending: false })
        .limit(3)
        .then(({ data }) => { if (data) setAiPosts(data); });
    });
  }, []);

  useEffect(() => {
    const loadCommunity = async () => {
      try {
        const db = await getSupabase();
        const { data: { session } } = await db.auth.getSession();
        const token = session?.access_token || '';
        const res = await fetch('/.netlify/functions/community-posts?limit=3', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        setCommunityPosts(data.posts || []);
      } catch {}
    };
    loadCommunity();
  }, []);

  const [cuisine, setCuisine] = useState('');
  const [restrictions, setRestrictions] = useState('');
  const [maxMinutes, setMaxMinutes] = useState(45);
  const [tonightRecipe, setTonightRecipe] = useState(null);
  const [tonightLoading, setTonightLoading] = useState(false);
  const [tonightError, setTonightError] = useState('');
  const [tonightSaved, setTonightSaved] = useState(false);

  const timeLabel = (m) => {
    if (m <= 15) return 'Quick (15 min)';
    if (m <= 30) return '30 min';
    if (m <= 45) return '45 min';
    if (m <= 60) return '1 hour';
    if (m <= 90) return '1.5 hours';
    return 'No limit';
  };

  const todayMeals = useMemo(() => mealPlan[todayKey()] || [], [mealPlan]);
  const nextMeal = todayMeals[0] || null;

  const dailyTargets = macroPlanner.dailyTargets || { calories: 2000, protein: 150, carbs: 200, fat: 70 };
  const todayCalories = useMemo(() => {
    return todayMeals.reduce((sum, mealName) => {
      const recipe = recipes.find(r => r.name.toLowerCase() === mealName.toLowerCase());
      return sum + (recipe?.macros?.calories || 0);
    }, 0);
  }, [todayMeals, recipes]);

  const expiringSoon = useMemo(() => {
    return inventory
      .filter(item => {
        const days = getExpiryDays(item.expiryDate);
        return days !== null && days <= 7;
      })
      .sort((a, b) => getExpiryDays(a.expiryDate) - getExpiryDays(b.expiryDate));
  }, [inventory]);

  const userName = useMemo(() => {
    if (!user?.email) return 'chef';
    const prefix = user.email.split('@')[0];
    return prefix.charAt(0).toUpperCase() + prefix.slice(1);
  }, [user]);

  const handleTonight = async () => {
    if (inventory.length === 0) {
      setTonightError('Add items to your Inventory first so we know what you have.');
      return;
    }
    const { allowed } = checkAILimit(tier, 'recipeAI');
    if (!allowed) {
      setTonightError('You\'ve reached your monthly Recipe AI limit. Upgrade your plan for more.');
      return;
    }
    setTonightLoading(true);
    setTonightError('');
    setTonightRecipe(null);
    setTonightSaved(false);
    try {
      const res = await fetch('/.netlify/functions/ai-tonight', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inventory: inventory.filter((i) => i.quantity > 0),
          cuisine: cuisine || undefined,
          restrictions: restrictions || undefined,
          maxMinutes: maxMinutes <= 90 ? maxMinutes : undefined,
          earnedMarks: tier === 'chef' ? earnedMarks : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate recipe');
      let recipe = data.recipe;
      if (!recipe && data.raw) {
        const jsonMatch = data.raw.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try { recipe = JSON.parse(jsonMatch[0]); } catch {}
        }
      }
      if (recipe) {
        setTonightRecipe(recipe);
        recordAIUsage('recipeAI');
      } else {
        setTonightError('Couldn\'t parse the recipe. Try again.');
      }
    } catch (err) {
      setTonightError(err.message || 'Something went wrong.');
    }
    setTonightLoading(false);
  };

  const saveTonightRecipe = () => {
    if (!tonightRecipe || !addRecipe) return;
    addRecipe({
      name: tonightRecipe.name,
      ingredients: (tonightRecipe.ingredients || []).map((i) => ({
        name: i.name,
        quantity: 0,
        unit: i.amount || 'units',
      })),
      procedure: tonightRecipe.steps || [],
      tags: ['Dinner'],
      servings: tonightRecipe.servings || 2,
      macros: { calories: 0, protein: 0, carbs: 0, fat: 0 },
    });
    setTonightSaved(true);
  };

  const missingIngredients = tonightRecipe?.ingredients?.filter((i) => !i.inPantry) || [];
  const pantryIngredients = tonightRecipe?.ingredients?.filter((i) => i.inPantry) || [];

  const addPost = () => {
    if (newPost.title.trim() && newPost.content.trim()) {
      setBlogPosts([{ ...newPost, date: new Date().toLocaleDateString() }, ...blogPosts]);
      setNewPost({ title: '', content: '' });
    }
  };

  const removePost = (index) => {
    setBlogPosts(blogPosts.filter((_, i) => i !== index));
  };

  const timeAgo = (d) => {
    const s = Math.floor((Date.now() - new Date(d)) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  };

  return (
    <div className="page-container">
      {/* ── Hero greeting ── */}
      <div className="dash-hero">
        <h1 className="dash-greeting">{getGreeting()}, {userName}</h1>
        <p className="dash-date">{formatDate()}</p>
      </div>

      {/* ── Dashboard cards ── */}
      <div className="dash-grid">
        <div className="dash-card" onClick={() => navigate('/meal-planner')} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && navigate('/meal-planner')}>
          <span className="dash-card-label">Next Meal</span>
          {nextMeal ? (
            <span className="dash-card-value">{nextMeal}</span>
          ) : (
            <span className="dash-card-empty">No meals planned</span>
          )}
          <span className="dash-card-hint">{todayMeals.length} meal{todayMeals.length !== 1 ? 's' : ''} today</span>
        </div>

        <div className="dash-card dash-card--macro" onClick={() => navigate('/macro-planner')} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && navigate('/macro-planner')}>
          <span className="dash-card-label">Macros Today</span>
          <MacroRing consumed={todayCalories} target={dailyTargets.calories} />
        </div>
      </div>

      {/* ── Expiring alert ── */}
      {expiringSoon.length > 0 && (
        <div className="dash-alert" onClick={() => navigate('/inventory')} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && navigate('/inventory')}>
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{expiringSoon.length} item{expiringSoon.length !== 1 ? 's' : ''} expiring this week</span>
          <ChevronRight size={14} aria-hidden="true" className="dash-alert-arrow" />
        </div>
      )}

      {/* ── Quick actions ── */}
      <div className="dash-actions">
        <button className="dash-action-btn" onClick={() => navigate('/meal-planner')}>
          <CalendarDays size={18} aria-hidden="true" />
          <span>Log Meal</span>
        </button>
        <button className="dash-action-btn" onClick={() => navigate('/inventory')}>
          <Package size={18} aria-hidden="true" />
          <span>Pantry</span>
        </button>
        <button className="dash-action-btn" onClick={() => navigate('/recipes')}>
          <BookOpen size={18} aria-hidden="true" />
          <span>Recipes</span>
        </button>
        <button className="dash-action-btn" onClick={() => navigate('/community/new')}>
          <Plus size={18} aria-hidden="true" />
          <span>Post</span>
        </button>
      </div>

      {/* ── Community preview ── */}
      {communityPosts.length > 0 && (
        <div className="dash-section">
          <div className="dash-section-header">
            <h2 className="dash-section-title">Community</h2>
            <Link to="/community" className="dash-section-link">View all <ChevronRight size={13} aria-hidden="true" /></Link>
          </div>
          <div className="dash-community-list">
            {communityPosts.map(post => (
              <div key={post.id} className="dash-community-card" onClick={() => navigate(`/community/post/${post.id}`)}>
                {post.image_url && <img src={post.image_url} alt="" loading="lazy" className="dash-community-img" />}
                <div className="dash-community-meta">
                  <span className="dash-community-user">{post.profile?.display_name || 'Chef'}</span>
                  <span className="dash-community-time">{timeAgo(post.created_at)}</span>
                </div>
                {post.caption && <p className="dash-community-caption">{post.caption.slice(0, 80)}{post.caption.length > 80 ? '…' : ''}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── What am I making tonight? ── */}
      <div className="settings-section">
        <h2 className="settings-section-title">What am I making tonight?</h2>
        <p className="settings-description">
          Pick a cuisine and any dietary restrictions — we'll build a recipe from what's in your pantry.
        </p>

        <div className="form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="tonight-cuisine" className="form-label">Cuisine</label>
            <select
              id="tonight-cuisine"
              value={cuisine}
              onChange={(e) => setCuisine(e.target.value)}
              className="form-select"
              style={{ width: '100%' }}
            >
              <option value="">Surprise me</option>
              {CUISINES.filter(Boolean).map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <label htmlFor="tonight-restrictions" className="form-label">Restrictions</label>
            <input
              id="tonight-restrictions"
              type="text"
              value={restrictions}
              onChange={(e) => setRestrictions(e.target.value)}
              placeholder="e.g. no dairy, gluten-free, vegetarian"
              className="form-input"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="tonight-time" className="form-label">How much time do you have?</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              id="tonight-time"
              type="range"
              min="15"
              max="120"
              step="15"
              value={maxMinutes}
              onChange={(e) => setMaxMinutes(parseInt(e.target.value))}
              style={{ flex: 1 }}
            />
            <span className="time-label-accent">{timeLabel(maxMinutes)}</span>
          </div>
        </div>

        <button
          onClick={handleTonight}
          className="btn btn-primary btn-block"
          disabled={tonightLoading}
          style={{ fontSize: '15px', padding: '12px' }}
        >
          {tonightLoading ? 'Thinking...' : 'What am I making tonight?'}
        </button>

        {tonightLoading && (
          <div className="tonight-progress">
            <div className="tonight-progress-bar" />
            <p className="tonight-progress-label">Scanning your pantry and crafting the perfect recipe…</p>
          </div>
        )}

        {tonightError && <p className="tonight-error">{tonightError}</p>}

        {tonightRecipe && (
          <div className="tonight-result">
            <h3 className="tonight-recipe-name">{tonightRecipe.name}</h3>
            {tonightRecipe.description && (
              <p className="tonight-recipe-description">{tonightRecipe.description}</p>
            )}

            <div className="fitness-stats-grid" style={{ marginBottom: '16px', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
              {tonightRecipe.difficulty && (
                <div className="fitness-stat-card">
                  <span className="fitness-stat-label">Difficulty</span>
                  <span className="fitness-stat-value" style={{ fontSize: '14px' }}>{tonightRecipe.difficulty}</span>
                </div>
              )}
              {tonightRecipe.servings && (
                <div className="fitness-stat-card">
                  <span className="fitness-stat-label">Servings</span>
                  <span className="fitness-stat-value" style={{ fontSize: '16px' }}>{tonightRecipe.servings}</span>
                </div>
              )}
              {tonightRecipe.prepTime && (
                <div className="fitness-stat-card">
                  <span className="fitness-stat-label">Prep</span>
                  <span className="fitness-stat-value" style={{ fontSize: '16px' }}>{tonightRecipe.prepTime}</span>
                </div>
              )}
              {tonightRecipe.cookTime && (
                <div className="fitness-stat-card">
                  <span className="fitness-stat-label">Cook</span>
                  <span className="fitness-stat-value" style={{ fontSize: '16px' }}>{tonightRecipe.cookTime}</span>
                </div>
              )}
            </div>

            {pantryIngredients.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 className="tonight-ingredient-heading tonight-ingredient-heading--have">In Your Pantry</h4>
                {pantryIngredients.map((ing, i) => (
                  <div key={i} className="list-item" style={{ padding: '6px 10px' }}>
                    <span className="list-item-name">{ing.name}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{ing.amount}</span>
                  </div>
                ))}
              </div>
            )}

            {missingIngredients.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 className="tonight-ingredient-heading tonight-ingredient-heading--need">You'll Need</h4>
                {missingIngredients.map((ing, i) => (
                  <div key={i} className="list-item" style={{ padding: '6px 10px' }}>
                    <span className="list-item-name">{ing.name}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{ing.amount}</span>
                      {addToShoppingList && (
                        <button onClick={() => addToShoppingList(ing.name, 1)} className="btn btn-ghost btn-sm" style={{ fontSize: '12px' }}>
                          + List
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tonightRecipe.steps && tonightRecipe.steps.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <h4 className="tonight-ingredient-heading">Instructions</h4>
                {tonightRecipe.steps.map((step, i) => (
                  <div key={i} className="tonight-step">
                    <span className="tonight-step-num">{i + 1}</span>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            )}

            {tonightRecipe.tips && (
              <div className="tonight-tip">
                <strong>Chef's tip:</strong> {tonightRecipe.tips}
              </div>
            )}

            <button
              onClick={saveTonightRecipe}
              className="btn btn-primary btn-block"
              disabled={tonightSaved}
              style={{ marginTop: '16px' }}
            >
              {tonightSaved ? 'Saved to Recipes' : 'Save to My Recipes'}
            </button>
          </div>
        )}
      </div>

      {/* ── Chef's Journal ── */}
      <div className="settings-section">
        <h2 className="settings-section-title">Chef's Journal</h2>
        <p className="settings-description">
          Record your cooking notes, experiments, and culinary discoveries.
        </p>

        <div className="form-group">
          <label htmlFor="post-title" className="form-label">Entry Title</label>
          <input
            id="post-title"
            type="text"
            placeholder="e.g., Perfecting the Pan Sear"
            value={newPost.title}
            onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="post-content" className="form-label">Journal Entry</label>
          <textarea
            id="post-content"
            placeholder="What did you learn today?"
            value={newPost.content}
            onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
            className="form-textarea"
          />
        </div>

        <button className="btn btn-primary btn-block" onClick={addPost}>Post Entry</button>
      </div>

      {/* ── Journal entries ── */}
      <div className="catalog-section">
        <div className="catalog-header">
          <h2 className="catalog-title">Journal Entries</h2>
          <span className="catalog-count">{blogPosts.length} entries</span>
        </div>

        {blogPosts.length === 0 ? (
          <div className="empty-state">
            <p className="empty-state-text">No journal entries yet. Start your first entry above!</p>
          </div>
        ) : (
          blogPosts.map((post, i) => (
            <div key={i} className="card journal-card">
              <div className="journal-card-header">
                <div>
                  <span className="journal-card-date">{post.date}</span>
                  <h3 className="journal-card-title">{post.title}</h3>
                </div>
                <button onClick={() => removePost(i)} className="btn btn-ghost btn-sm" aria-label={`Delete ${post.title}`}>
                  <X size={14} aria-hidden="true" />
                </button>
              </div>
              <p className="journal-card-body">{post.content}</p>
            </div>
          ))
        )}
      </div>

      {/* ── Protocol Library ── */}
      {aiPosts.length > 0 && (
        <div className="catalog-section">
          <div className="catalog-header">
            <h2 className="catalog-title">Protocol Library</h2>
            <Link to="/blog" className="dash-section-link">View all <ChevronRight size={13} aria-hidden="true" /></Link>
          </div>
          {aiPosts.map((post) => (
            <Link key={post.slug} to={`/blog/${post.slug}`} className="protocol-card-link">
              <div className="card protocol-card">
                <span className="protocol-card-date">
                  {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <h3 className="protocol-card-title">{post.title}</h3>
                {post.focus_ingredients?.length > 0 && (
                  <div className="protocol-card-tags">
                    {post.focus_ingredients.slice(0, 3).map((ing) => (
                      <span key={ing} className="protocol-card-tag">{ing}</span>
                    ))}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Home;
